import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { Loader, Send } from "lucide-react";
import moment from "moment-timezone";
import { CommonDestinationTimezone } from "@/lib/utils";
import { useSendDormanMarginCall } from "@/lib/api/reportSettings";

interface SendDormanMarginCallModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SendDormanMarginCallModal({ isOpen, onClose }: SendDormanMarginCallModalProps) {
  const { toast } = useToast();
  const yesterdayMoment = moment.tz(CommonDestinationTimezone).subtract(1, 'day');
  const [selectedDate, setSelectedDate] = useState<string>(yesterdayMoment.format("YYYY-MM-DD"));
  const [isSending, setIsSending] = useState(false);
  
  const sendDormanMarginCall = useSendDormanMarginCall();
  
  const handleSend = async () => {
    if (isSending) return;
    setIsSending(true);
    
    try {
      const result = await sendDormanMarginCall({
        date: selectedDate
      });
      
      if (!result.success) {
        console.error("Failed to send Dorman margin call report", result);
        toast({ 
          title: "Send Failed", 
          description: result.message || "Failed to send Dorman margin call report.", 
          variant: "destructive" 
        });
        return;
      }
      
      toast({ 
        title: "Report Sent", 
        description: result.message || "Margin call report sent successfully.", 
        variant: "default" 
      });
    } catch (error) {
      console.error("Failed to send Dorman margin call report", error);
      toast({ 
        title: "Send Failed", 
        description: String(error), 
        variant: "destructive" 
      });
    } finally {
      setIsSending(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open && !isSending) {
        onClose();
      }
    }}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle>Send Manual Dorman Report</DialogTitle>
          <DialogDescription>
            Select a date to manually send the Dorman margin call report
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          <div className="space-y-2">
            <Label>Report Date</Label>
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              max={yesterdayMoment.format("YYYY-MM-DD")}
            />
          </div>
          
          <div className="bg-muted p-3 rounded-md space-y-1">
            <p className="text-sm font-medium">Selected Date:</p>
            <p className="text-sm text-muted-foreground">
              {moment(selectedDate).format("MMMM D, YYYY")}
            </p>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isSending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSend}
              disabled={isSending || !selectedDate || !moment(selectedDate).isValid()}
              className="flex items-center gap-2"
            >
              {isSending ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Send Report
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
